import * as DG from 'datagrok-api/dg';
import { sequenceChemSimilarity } from '../../monomer-works/monomer-utils';
import { splitAlignedSequences } from '../splitter';
import { GAP_SYMBOL } from './consts';
export var SCORE;
(function (SCORE) {
    SCORE["IDENTITY"] = "identity";
    SCORE["SIMILARITY"] = "similarity";
})(SCORE || (SCORE = {}));
/** Performs transformations and sequence scoring.
 * @param {DG.DataFrame} table Table to attach results to.
 * @param {DG.Column<string>} col Sequences column to score. Must have Macromolecule semantic type.
 * @param {string} ref Reference sequence to score against.
 * @param {SCORE} scoring Scoring method.
 * @param {ISeqHelper} seqHelper Helper for sequence operations.
 * @returns {DG.Column<number>} Scores column. */
export async function calculateScores(table, col, ref, scoring, seqHelper) {
    const splitSeqDf = splitAlignedSequences(col, seqHelper);
    const srcSh = seqHelper.getSeqHandler(col);
    const refCol = srcSh.getNewColumnFromList('ref', [ref]);
    const refUh = seqHelper.getSeqHandler(refCol);
    const refSplitted = refUh.getSplitted(0); // ref is at 0
    const scoresCol = scoring === SCORE.IDENTITY ? calculateIdentity(refSplitted, splitSeqDf) :
        scoring === SCORE.SIMILARITY ? await calculateSimilarity(refSplitted, splitSeqDf, seqHelper) : null;
    if (scoresCol == null)
        throw new Error(`In bio library: Unknown sequence scoring method: ${scoring}`);
    scoresCol.name = table.columns.getUnusedName(scoresCol.name);
    table.columns.add(scoresCol);
    return scoresCol;
}
/** Calculates identity scores as fraction of matching monomers on the same position.
 * @param {ISeqSplitted} reference Splitted reference sequence.
 * @param {DG.DataFrame} positionsDf Table which only contains position columns with semantic type Monomer.
 * @returns {DG.Column<number>} Scores column. */
export function calculateIdentity(reference, positionsDf) {
    const numPositions = positionsDf.columns.length;
    const positionCols = new Array(numPositions);
    const positionEmptyCategories = new Array(numPositions);
    const categoryIndexesTemplate = new Array(numPositions);
    for (let posIdx = 0; posIdx < numPositions; ++posIdx) {
        const posCol = positionsDf.columns.byIndex(posIdx);
        positionCols[posIdx] = posCol.getRawData();
        positionEmptyCategories[posIdx] = posCol.categories.indexOf('');
        const refM = posIdx < reference.length ? reference.getOriginal(posIdx) : GAP_SYMBOL;
        categoryIndexesTemplate[posIdx] = posCol.categories.indexOf(refM);
    }
    const identityScoresCol = DG.Column.float('Identity', positionsDf.rowCount);
    const identityScoresData = identityScoresCol.getRawData();
    for (let rowIndex = 0; rowIndex < positionsDf.rowCount; ++rowIndex) {
        identityScoresData[rowIndex] = 0;
        for (let posIdx = 0; posIdx < reference.length; ++posIdx) {
            const positionCol = positionCols[posIdx];
            const categoryIndex = positionCol ? positionCols[posIdx][rowIndex] : 0;
            if (categoryIndex === categoryIndexesTemplate[posIdx])
                ++identityScoresData[rowIndex];
        }
        identityScoresData[rowIndex] /= reference.length;
    }
    return identityScoresCol;
}
/** Calculates similarity scores as sum of monomer fingerprint similarities on the same position.
 * @param {ISeqSplitted} reference Splitted reference sequence.
 * @param {DG.DataFrame} positionsDf Table which only contains position columns with semantic type Monomer.
 * @return {DG.Column<number>} Scores column. */
export async function calculateSimilarity(reference, positionsDf, seqHelper) {
    const monomerColumns = positionsDf.columns.toList();
    const scoresCol = await sequenceChemSimilarity(monomerColumns, reference, seqHelper);
    return scoresCol;
}
//# sourceMappingURL=scoring.js.map